// OneDrive:IgnoreCodeCoverage

import UtilsFeatures, { IFeature } from './Features';
import { isSPFeatureEnabled } from './FeaturesBasic';
import * as AddressParser from '../navigation/AddressParser';
import DataStore from '../models/store/BaseDataStore';
import DataStoreCachingType from '../models/store/DataStoreCachingType';
import { Killswitch } from '../killswitch/Killswitch';

export const ENABLE_PARAM_KEY = 'enableFeatures';
export const DISABLE_PARAM_KEY = 'disableFeatures';
const STORE_KEY = 'Store';

const AllowFeatureOverrides: IFeature = { ODB: 587, ODC: 'AllowFeatureOverrides', Fallback: false };
const SPODebugOnlyCookieRedirect: IFeature = { ODB: 357, ODC: null, Fallback: false };

const STORE_NAME = "FeatureOverrides";
let _overrides: { [key: string]: boolean } = {};
let _store = new DataStore(STORE_NAME, DataStoreCachingType.session);

// Evaluate the AllowFeatureOverrides feature only once since we are going to need it a lot
let _canOverride = UtilsFeatures.isFeatureEnabled(AllowFeatureOverrides)
    // anywhere debugonlycookieredirect is enabled, we allow feature overrides too
    || UtilsFeatures.isFeatureEnabled(SPODebugOnlyCookieRedirect);

if (typeof DEBUG !== 'undefined' && DEBUG) {
    _canOverride = true;
}

const ENABLE_COOKIE_NAME = `${STORE_NAME}_${ENABLE_PARAM_KEY}`;
const DISABLE_COOKIE_NAME = `${STORE_NAME}_${DISABLE_PARAM_KEY}`;

function _getCookieValue(cookieName: string) {
    const val = document.cookie.match('(^|;)\\s*' + cookieName + '\\s*=\\s*([^;]+)');
    return val ? val.pop() : "";
}

function _setFeatureSettingsToCookie(overrides: { [key: string]: boolean }) {
    let enableFeatures = [];
    let disableFeatures = [];
    for (let key in overrides) {
        (overrides[key] ? enableFeatures : disableFeatures).push(key);
    }
    document.cookie = `${ENABLE_COOKIE_NAME}=${enableFeatures.join(",")};path=/`;
    document.cookie = `${DISABLE_COOKIE_NAME}=${disableFeatures.join(",")};path=/`;
}

function _getFeatureSettingsFromCookie() {
    let overrides = {};
    const enableFeatures = _getCookieValue(ENABLE_COOKIE_NAME);
    if (enableFeatures) {
        for (const param of enableFeatures.split(',')) {
            overrides[param] = true;
        }
    }
    const disableFeatures = _getCookieValue(DISABLE_COOKIE_NAME);
    if (disableFeatures) {
        for (const param of disableFeatures.split(',')) {
            overrides[param] = false;
        }
    }
    return overrides;
}

function init() {
    const useSessionStorage = Killswitch.isActivated("CA76C674-5DEE-4C8A-9EDF-82C275E0CF01", "11/27/2018", "Don't save enable/disable features query params to session storage");
    if (useSessionStorage) {
        // Load existing values
        const overrides = _store.getValue<{ [key: string]: boolean }>(STORE_KEY);
        if (overrides) {
            _overrides = overrides;
        } else {
            _overrides = {};
        }
    } else {
        _overrides = _getFeatureSettingsFromCookie();
    }
    // Parse the url and add any valid feature params in the override map
    const uri = location.search ? location.search.substring(1) : '';
    const params = AddressParser.deserializeQuery(uri);
    const enableFeatures = params[ENABLE_PARAM_KEY];
    if (enableFeatures) {
        for (const param of enableFeatures.split(',')) {
            _overrides[param] = true;
        }
    }

    const disableFeatures = params[DISABLE_PARAM_KEY];
    if (disableFeatures) {
        for (const param of disableFeatures.split(',')) {
            _overrides[param] = false;
        }
    }
    if (useSessionStorage) {
        _store.setValue(STORE_KEY, _overrides);
    } else {
        _setFeatureSettingsToCookie(_overrides);
    }
}

init();

export default class FeatureOverrides {
    public static AllowFeatureOverrides: IFeature = AllowFeatureOverrides;
    public static SPODebugOnlyCookieRedirect: IFeature = SPODebugOnlyCookieRedirect;

    public static get Overrides(): { [key: string]: boolean } {
        return _overrides;
    }

    public static isFeatureEnabled(feature: IFeature, expFeatures?: number[]): boolean {
        // Check if there is an overriden value for the given feature
        if (_canOverride) {
            if (<any>feature.ODB in _overrides) {
                return _overrides[<any>feature.ODB];
            } else if (feature.ODC in _overrides) {
                return _overrides[<any>feature.ODC];
            }
        }

        if (expFeatures) {
            return isSPFeatureEnabled(feature, expFeatures);
        }
        return UtilsFeatures.isFeatureEnabled(feature);
    }
}
